import { type Edge, type Node } from '@xyflow/react';

export const EDGE_COLOR = '#94a3b8';

import type { IComponentSchema } from '../../types';

export interface IFlowNodeData extends Record<string, unknown> {
  label: string;
  subLabel?: string;
  category: string;
  /** Resolved type code — the termId, or (groups-as-nodes) the group key — for $node.code. */
  code?: string;
  /** Groups-as-nodes: the group's codes, exposed as $node.groupCodes for panel filtering. */
  groupCodes?: string[];
  /**
   * Groups-as-nodes: a ready json-logic filter (`{ in: [{var}, groupCodes] }`)
   * exposed as $node.operationFilter, so the panel's type reference-select can
   * bind it straight into `modelInstanceFilters` (arrays/objects can't be built
   * from string templates in the schema parser).
   */
  operationFilter?: unknown;
  color: string;
  icon: string;
  item: Record<string, unknown>;
  cardSchema?: IComponentSchema<Record<string, unknown>>;
  /** Set by validation: node is missing required fields (Requirements). */
  invalid?: boolean;
  /** Set by validation: the requirement labels this node violates. */
  issues?: string[];
  /** Preview mode: the node's role (as-mfg, mp-proc, …). */
  role?: string;
  /** Preview mode: side operations shown on the node (not their own nodes). */
  sides?: Array<{
    role: string;
    id: string;
    mbo?: Record<string, unknown>;
    icon?: string;
    color?: string;
    label?: string;
  }>;
  /** Preview mode: resource rows this node is linked from (multi-resource). */
  resources?: Array<{ root: string; id: string; label?: string }>;
}

export type FlowNodeType = Node<IFlowNodeData, 'flowNode'>;

export const getPath = (obj: unknown, path?: string): unknown => {
  if (obj == null || !path) return undefined;
  return path
    .split('.')
    .reduce<unknown>(
      (acc, key) =>
        acc != null ? (acc as Record<string, unknown>)[key] : undefined,
      obj
    );
};

export const setPath = <T extends Record<string, unknown>>(
  obj: T,
  path: string,
  value: unknown
): T => {
  const keys = path.split('.');
  const clone: Record<string, unknown> = { ...obj };
  let cursor = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    cursor[keys[i]] = { ...((cursor[keys[i]] as Record<string, unknown>) ?? {}) };
    cursor = cursor[keys[i]] as Record<string, unknown>;
  }
  cursor[keys[keys.length - 1]] = value;
  return clone as T;
};

/**
 * Card/panel schemas live inside the FlowChart field's x-component-props, so the
 * screen's Formily engine tries to compile any `{{ ... }}` in them (with no $row
 * in scope → error). Authors write Formily-safe `{% ... %}`; we convert them back
 * to `{{ ... }}` here, right before our own parser resolves them against `$row`.
 */
export { toRuntimeTemplates } from '../node-panel/toRuntimeTemplates';

/** Longest-path layering: layer(target) = max(layer(source) + 1). */
export const computeLayers = (
  ids: string[],
  edges: Edge[]
): Map<string, number> => {
  const layer = new Map<string, number>(ids.map(id => [id, 0]));
  for (let pass = 0; pass < ids.length; pass++) {
    let changed = false;
    for (const e of edges) {
      const next = (layer.get(e.source) ?? 0) + 1;
      if (next > (layer.get(e.target) ?? 0)) {
        layer.set(e.target, next);
        changed = true;
      }
    }
    if (!changed) break;
  }
  return layer;
};

export const isPosition = (v: unknown): v is { x: number; y: number } =>
  !!v &&
  typeof (v as { x?: unknown }).x === 'number' &&
  typeof (v as { y?: unknown }).y === 'number';

